import React, { useEffect, useState, useRef } from "react";
import {
  FaArrowRight,
  FaCalendarAlt,
  FaClipboardList,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaShieldAlt,
  FaUserCircle,
} from "react-icons/fa";
import { IoLogOutOutline } from "react-icons/io5";
import { MdWarning } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import Receipt from "../components/receipt";
import formatCustomDate from "../helpers/customDateFormat";
import HandleLogout from "../helpers/logout";

const getOperatorPhotoUrl = (photo, eventId) => {
  if (!photo) return "";
  if (
    photo.startsWith("data:") ||
    photo.startsWith("http://") ||
    photo.startsWith("https://")
  ) {
    return photo;
  }
  const cleanBase = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  
  if (
    !photo.includes("/app/media/") &&
    !photo.includes("app/media/") &&
    !photo.includes("/media/") &&
    !photo.includes("media/") &&
    eventId
  ) {
    return `${cleanBase}/app/media/events/${eventId}/${photo}`;
  }
  
  const cleanPath = photo.startsWith("/") ? photo : `/${photo}`;
  return `${cleanBase}${cleanPath}`;
};

function Homepage() {
  const testSampleItems = {
    name: "Test Name",
    family_name: "Test Family",
    occupation: "Business",
    mobile: "9876543210",
    village: "Test Village",
    city: "Test City",
    asset_type: "cash",
    amount: "1001",
    receipt_no: "TEST-001",
    denomination_amount: JSON.stringify({"500": 2, "1": 1})
  };
  const testComponentRef = useRef();
  const testReactToPrintFn = useReactToPrint({
    contentRef: testComponentRef,
    documentTitle: "TestReceipt",
  });

  const logo = require("../assets/images/logo.jpeg");
  const navigate = useNavigate();
  const [eventInfo, setEventInfo] = useState({
    event_type: "",
    event_date: "",
    event_address: "",
    event_city: "",
    event_state: "",
    event_pincode: "",
    operator_name: "",
    operator_mobile: "",
    operator_photo: "",
    event_id: "",
    customer_name: "",
    customer_mobile: "",
  });
  const [showQrModal, setShowQrModal] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("event-info");
    if (stored) {
      const parsed = JSON.parse(stored);
      setEventInfo({
        event_type: parsed.event_type || "",
        event_date: parsed.event_date || "",
        event_address: parsed.event_address || "",
        event_city: parsed.event_city || "",
        event_state: parsed.event_state || "",
        event_pincode: parsed.event_pincode || "",
        operator_name: parsed.operator_name || "",
        operator_mobile: parsed.operator_mobile || "",
        operator_photo: parsed.operator_photo || "",
        event_id: parsed.event_id || "",
        customer_name: parsed.customer_name || "",
        customer_mobile: parsed.customer_mobile || "",
        organisation_wallet_balance: parsed.organisation_wallet_balance || 0,
        moi_confirmation_sms: parsed.moi_confirmation_sms || 0,
      });
    }
  }, []);

  const operatorPhotoUrl = getOperatorPhotoUrl(eventInfo.operator_photo, eventInfo.event_id);

  const getOperatorInitials = () => {
    if (!eventInfo.operator_name) return "?";
    const parts = eventInfo.operator_name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const fullAddress = [
    eventInfo.event_address,
    eventInfo.event_city,
    eventInfo.event_state,
    eventInfo.event_pincode,
  ]
    .filter(Boolean)
    .join(", ");

  const getCustomerBaseUrl = () => {
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      if (hostname.includes("localhost") || hostname === "127.0.0.1") {
        return "http://localhost:3001";
      }
      if (hostname.includes("demo")) {
        return "https://customer-demo.digitalmoitech.com";
      }
      if (hostname.includes("digitalmoitech.com")) {
        return "https://customer.digitalmoitech.com";
      }
    }
    if (process.env.REACT_APP_CUSTOMER_URL) {
      return process.env.REACT_APP_CUSTOMER_URL;
    }
    return "https://customer.digitalmoitech.com";
  };
  const customerBaseUrl = getCustomerBaseUrl();
  const quickTokenUrl = `${customerBaseUrl}/quick-token?event_id=${eventInfo.event_id}`;
  const qrCodeImgUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(quickTokenUrl)}`;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-12">
      <style>{`
        @keyframes cardRise {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .dot-grid {
          background-image: radial-gradient(circle, rgba(217,119,6,0.04) 1px, transparent 1px);
          background-size: 24px 24px;
        }
        @media print {
          .no-print {
            display: none !important;
          }
          .print-only {
            display: flex !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
        .print-only {
          display: none;
        }
      `}</style>

      {/* Main UI Wrapper (Hidden during Printing) */}
      <div className="no-print">
        {/* Hero Header Area */}
        <div className="relative overflow-hidden bg-slate-955 px-4 py-8 sm:px-6 lg:px-8 shadow-md" style={{ background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' }}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(217,119,6,0.15),_transparent_35%)] pointer-events-none" />
          <div className="relative mx-auto max-w-5xl">
            <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white shadow-md">
                  <img src={logo} alt="Digital Moi Tech" className="h-9 w-9 rounded-lg object-cover" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-500">
                    Digital Moi Tech
                  </p>
                  <h1 className="text-xl font-bold tracking-tight text-white sm:text-2xl">
                    Entry Operations Portal
                  </h1>
                </div>
              </div>
              <button
                onClick={() => HandleLogout()}
                className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold text-slate-300 transition hover:bg-white/10 hover:text-white"
              >
                <IoLogOutOutline className="text-lg" />
                Logout
              </button>
            </header>
          </div>
        </div>

        {/* Main Workspace */}
        <div className="dot-grid min-h-[calc(100vh-120px)] px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl space-y-6">
            {/* Low Wallet Balance Warning */}
            {eventInfo?.organisation_wallet_balance < 1000 && eventInfo?.moi_confirmation_sms == 1 && (
              <div className="flex items-center gap-3 rounded-xl border-2 border-orange-500 bg-orange-50 px-4 py-3 text-orange-700 shadow-lg animate-pulse" style={{ animation: "fadeIn 0.4s ease both" }}>
                <MdWarning size={22} className="shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-bold">
                    ⚠️ Low Wallet Balance
                  </p>
                  <p className="text-xs mt-0.5 text-orange-600">
                    Your balance is running out. Please recharge. If the balance runs out, WhatsApp messages will not be sent.
                  </p>
                </div>
              </div>
            )}

            {/* Top Session Summary Bar */}
            <div 
              style={{ animation: "fadeIn 0.4s ease both" }}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]"
            >
              <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[10px] font-bold text-slate-550 uppercase tracking-widest">
                    Active Event Session
                  </span>
                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 ring-1 ring-amber-100/50">
                    Event #{eventInfo.event_id || "Pending"}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {eventInfo.event_id && (
                    <button
                      onClick={() => setShowQrModal(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-amber-600 bg-white px-3 py-1 text-xs font-bold text-amber-700 shadow-sm transition hover:bg-amber-50"
                    >
                      Print Guest QR
                    </button>
                  )}
                  {eventInfo.operator_name && (
                    <div className="flex items-center gap-2 text-xs font-bold text-slate-700 bg-slate-100 px-3 py-1 rounded-lg">
                      <span className="text-slate-500 font-medium">Operator:</span> {eventInfo.operator_name}
                    </div>
                  )}
                </div>
              </div>
            
            <div className="p-6 grid gap-6 sm:grid-cols-3">
              <div className="sm:col-span-2 space-y-2">
                <h2 className="text-xl font-bold text-slate-900">
                  {eventInfo.event_type || "No active event"}
                </h2>
                <p className="flex items-start gap-1.5 text-xs text-slate-500 leading-relaxed">
                  <FaMapMarkerAlt className="mt-0.5 shrink-0 text-amber-600" />
                  <span>{fullAddress || "Event location details pending."}</span>
                </p>
              </div>
              <div className="border-t sm:border-t-0 sm:border-l border-slate-100 pt-4 sm:pt-0 sm:pl-6 space-y-2 text-xs">
                <div className="flex items-center gap-2 text-slate-600">
                  <FaCalendarAlt className="text-amber-600 shrink-0" />
                  <span>{formatCustomDate(eventInfo.event_date) || "Date pending"}</span>
                </div>
                {eventInfo.customer_name && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <FaUserCircle className="text-amber-600 shrink-0" />
                    <span>Customer: {eventInfo.customer_name}</span>
                  </div>
                )}
                {eventInfo.operator_mobile && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <FaPhoneAlt className="text-amber-600 shrink-0" />
                    <span>Mobile: {eventInfo.operator_mobile}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Primary Operations Grid - Front & Center Buttons */}
          <div 
            style={{ animation: "cardRise 0.5s cubic-bezier(0.22,1,0.36,1) both" }}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {/* Enter Moi Action Card Button */}
            <Link
              to="/moibook"
              className="group relative flex flex-col justify-between overflow-hidden rounded-3xl border-2 border-slate-200 bg-white p-6 shadow-md transition duration-200 hover:-translate-y-1 hover:border-amber-500 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-amber-500/10 no-underline"
            >
              <div className="space-y-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-2xl text-amber-600 shadow-sm transition group-hover:scale-105 group-hover:bg-amber-100/50">
                  <FaMoneyBillWave />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-2xl font-bold tracking-tight text-slate-900 group-hover:text-amber-600 transition">
                      Enter Moi
                    </h3>
                    <span className="text-xs font-semibold text-slate-400">பதிவு செய்க</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    Start recording gift contributions for this event. Quick access to cash, online, ornaments, and item entries.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex items-center justify-between">
                <div className="inline-flex items-center gap-2 rounded-xl bg-amber-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition group-hover:bg-amber-700">
                  Open Entry Book
                  <FaArrowRight className="text-xs transition group-hover:translate-x-1" />
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Quick Action
                </span>
              </div>
              <div className="absolute top-0 right-0 h-24 w-24 bg-gradient-to-bl from-amber-500/5 to-transparent rounded-bl-full pointer-events-none" />
            </Link>

            {/* Moi List Action Card Button */}
            <Link
              to="/moilist"
              className="group relative flex flex-col justify-between overflow-hidden rounded-3xl border-2 border-slate-200 bg-white p-6 shadow-md transition duration-200 hover:-translate-y-1 hover:border-slate-800 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-slate-900/10 no-underline"
            >
              <div className="space-y-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl text-slate-850 shadow-sm transition group-hover:scale-105 group-hover:bg-slate-200/50">
                  <FaClipboardList />
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-2xl font-bold tracking-tight text-slate-900 group-hover:text-slate-850 transition">
                      Moi List
                    </h3>
                    <span className="text-xs font-semibold text-slate-400">பட்டியல்</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    Review and verify previously captured entries, check statistics, search records, or generate excel/pdf reports.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex items-center justify-between">
                <div className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-bold text-white shadow-sm transition group-hover:bg-slate-800">
                  View Entry List
                  <FaArrowRight className="text-xs transition group-hover:translate-x-1" />
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Records List
                </span>
              </div>
              <div className="absolute top-0 right-0 h-24 w-24 bg-gradient-to-bl from-slate-950/5 to-transparent rounded-bl-full pointer-events-none" />
            </Link>



            {/* Test Printer Action Card Button */}
            <button
              type="button"
              onClick={testReactToPrintFn}
              className="group relative flex flex-col justify-between overflow-hidden rounded-3xl border-2 border-slate-200 bg-white p-6 shadow-md transition duration-200 hover:-translate-y-1 hover:border-indigo-500 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-indigo-500/10 text-left"
            >
              <div className="space-y-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-2xl text-indigo-600 shadow-sm transition group-hover:scale-105 group-hover:bg-indigo-100/50">
                  🖨️
                </div>
                <div>
                  <div className="flex items-baseline gap-2">
                    <h3 className="text-2xl font-bold tracking-tight text-slate-900 group-hover:text-indigo-600 transition">
                      Test Printer
                    </h3>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    Print a sample receipt to ensure your printer is correctly configured and working.
                  </p>
                </div>
              </div>
              <div className="mt-8 flex items-center justify-between">
                <div className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition group-hover:bg-indigo-700">
                  Print Sample
                  <FaArrowRight className="text-xs transition group-hover:translate-x-1" />
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Utility
                </span>
              </div>
              <div className="absolute top-0 right-0 h-24 w-24 bg-gradient-to-bl from-indigo-500/5 to-transparent rounded-bl-full pointer-events-none" />
            </button>
          </div>

          {/* Footer Info */}
          <div className="pt-6 text-center">
            <p className="text-[10px] text-slate-400">
              DIGITAL MOI TECH &middot; Live Operations Register &middot; Contributions are secured and backed up in real time
            </p>
          </div>

        </div>
      </div>
      </div>

      {/* 1. Print Only Poster (Hidden on screen, visible during printing) */}
      {showQrModal && (
        <div className="print-only w-full h-full flex flex-col items-center justify-center text-center p-12 bg-white">
          <div className="mb-6">
            <span className="rounded-full bg-slate-100 border border-slate-200 px-4 py-1.5 text-xs font-black uppercase tracking-widest text-slate-700">
              Digital Moi Tech
            </span>
          </div>
          <h1 className="text-3xl font-black text-slate-950 leading-tight">
            Scan to Create Your Token
          </h1>
          <p className="text-lg font-bold text-amber-600 mt-2">
            டோக்கன் பெற ஸ்கேன் செய்யவும்
          </p>
          
          <div className="my-10 p-4 bg-white border-2 border-slate-200 rounded-3xl shadow-sm">
            <img
              src={qrCodeImgUrl}
              alt="Guest Token QR Code"
              className="w-72 h-72 object-contain"
            />
          </div>

          <div className="w-full max-w-md space-y-3 rounded-2xl bg-slate-50 p-6 text-sm font-semibold text-slate-700 border border-slate-200 mb-8">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-bold">Event Details</p>
            <p className="text-lg font-black text-slate-950">{eventInfo.event_type}</p>
            <p className="text-base text-slate-700">{formatCustomDate(eventInfo.event_date)}</p>
            <p className="text-sm font-medium text-slate-555 leading-relaxed">{fullAddress}</p>
          </div>

          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-relaxed max-w-sm">
            Please scan this QR code with your mobile camera or scanner to fill the token form directly.
          </p>
        </div>
      )}

      {/* 2. On-Screen Preview Modal (Visible on screen, hidden during printing) */}
      {showQrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm no-print">
          <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-slate-100 bg-white p-6 shadow-2xl animate-[fadeIn_0.2s_ease] flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4 shrink-0">
              <h3 className="text-base font-bold text-slate-800">Guest QR Code Poster</h3>
              <button
                onClick={() => setShowQrModal(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition"
              >
                ✕
              </button>
            </div>

            {/* Poster Preview on Screen */}
            <div className="flex-1 overflow-y-auto pr-1">
              <div className="flex flex-col items-center text-center p-6 border border-slate-150 rounded-2xl bg-slate-50/50">
                <div className="mb-3">
                  <span className="rounded-full bg-amber-50 border border-amber-100 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700">
                    Digital Moi Tech
                  </span>
                </div>
                <h2 className="text-lg font-black text-slate-900 leading-tight">
                  Scan to Create Token
                </h2>
                <p className="text-xs font-bold text-amber-600 mt-0.5">
                  டோக்கன் பெற ஸ்கேன் செய்யவும்
                </p>
                
                {/* QR Image */}
                <div className="my-4 p-2 bg-white border border-slate-100 rounded-xl shadow-sm">
                  <img
                    src={qrCodeImgUrl}
                    alt="Guest Token QR Code"
                    className="w-44 h-44 object-contain"
                  />
                </div>

                {/* Event details */}
                <div className="w-full space-y-1.5 rounded-xl bg-white p-4 text-[11px] font-semibold text-slate-700 border border-slate-100 mb-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">Event Details</p>
                  <p className="text-xs font-black text-slate-900">{eventInfo.event_type}</p>
                  <p>{formatCustomDate(eventInfo.event_date)}</p>
                  <p className="line-clamp-2 text-slate-500 font-medium">{fullAddress}</p>
                </div>

                <p className="text-[9px] font-bold text-slate-400 leading-relaxed uppercase tracking-wider">
                  Scan with any QR scanner to create token
                </p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-4 flex justify-end gap-3 border-t border-slate-100 pt-4 shrink-0">
              <button
                onClick={() => setShowQrModal(false)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 shadow-sm transition"
              >
                Close
              </button>
              <button
                onClick={() => window.print()}
                className="rounded-xl border border-amber-600 bg-amber-600 px-5 py-2.5 text-xs font-bold text-white hover:bg-amber-700 shadow-md shadow-amber-600/10 transition"
              >
                Print Poster
              </button>
            </div>
          </div>
        </div>
      )}


      <div style={{ display: "none" }}>
        <Receipt
          ref={testComponentRef}
          items={testSampleItems}
          eventDetails={eventInfo}
        />
      </div>
    </div>
  );
}

export default Homepage;
